<template>
<div>
    <header>
        <a href="#" @click.prevent="goBack" class="back"></a>
        <div>
            <router-link :to="`/tournament/${$route.params.id}/rename`" class="btn-secondary" 
                         v-if="user.id == tournament.organizer_id">
                Umbenennen
            </router-link>
        </div>
        <h1>{{ tournament.name }}</h1>
    </header>

    <section>
        <h2>Organisator</h2>
        <router-link :to="'/profile/' + tournament.organizer_id" class="item">
            <dp :who="tournament.organizer" />
            <h3>{{tournament.organizer.firstname}} {{tournament.organizer.lastname}}</h3>
            <div class="clear"></div>
        </router-link>
    </section>

    <section v-if="tournament.status == 'waiting'">
        <h2>Einladungen</h2>
        <router-link v-for="inv in tournament.invitations" :key="inv.id" 
                     :to="'/profile/' + inv.user_id" class="item">
            <dp :who="inv.invitee" />
            <h3>{{inv.invitee.firstname}} {{inv.invitee.lastname}}</h3>
            <small v-if="inv.accepted">Akzeptiert</small>
            <small v-else>Ausstehend</small>
            <div class="clear"></div>
        </router-link>
    </section>

    <template v-if="tournament.status == 'completed'">
        <section>
            <h2>Gewinner</h2>
            <router-link :to="'/profile/' + winner.id" class="item">
                <dp :who="winner" />
                <h3>{{ winner | name }}</h3>
            </router-link>
        </section>

        <section>
            <h2>Zweitplatzierter</h2>
            <router-link :to="'/profile/' + second.id" class="item">
                <dp :who="second" />
                <h3>{{ second | name }}</h3>
            </router-link>
        </section>
    </template>

    <template v-if="tournament.status != 'waiting'">
        <section>
            <header>
                <h2>Gruppe</h2>
                <div class="choice">
                    <a href="#" @click.prevent="groupFilter = groupNo" 
                        v-bind:class="{ 'btn-secondary': groupFilter == groupNo }"
                        v-for="(group, groupNo) in groups">
                        {{ groupNo | numToLetter }}
                    </a>
                </div>
            </header>
            <table class="ranking">
                <thead>
                    <tr>
                        <th></th>
                        <th>Spieler</th>
                        <th>Punkte</th>
                        <th>Sätze</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(player, rank) in tournament.rankings[groupFilter]">
                        <td class="rank">{{ rank + 1 }}.</td>
                        <td>
                            <router-link :to="'/profile/' + player.user_id">
                                <h3>{{ participants[player.user_id] | name }}</h3>
                            </router-link>
                        </td>
                        <td class="points">{{ player.points }}</td>
                        <td class="games">{{ player.games }}</td>
                    </tr>
                </tbody>
            </table>
            <div class="matches">
                <router-link :to="'/match/' + match.id" class="item"
                             v-for="match in tournament.matches" :key="match.id"
                             v-if="match.group === groupFilter">
                    <h3>
                        {{ match.player_a | name }} vs {{ match.player_b | name }}
                    </h3>
                    <div class="score" v-if="match.status == 'completed'">
                        {{ match.points_a }}:{{ match.points_b }}
                    </div>
                </router-link>
            </div>
        </section>

        <section>
            <h2>Ausscheidungsphase</h2>
            <router-link :to="'/match/' + match.id" 
                v-for="match in tournament.matches" :key="match.id"
                v-if="match.player_a_id !== null && match.player_b_id !== null && match.group === null" class="item">
                <h3>
                    {{ match.player_a | name }} vs {{ match.player_b | name }}
                </h3>
                <small>
                    {{ match.level | roundName }}
                </small>
                <small v-if="match.status == 'completed'">
                    {{ match.points_a }}:{{ match.points_b }}
                </small>
            </router-link>
        </section>
    </template>

    <section v-if="user.id == tournament.organizer_id">
        <a href="#" @click.prevent="deleteTournament" class="btn btn-secondary">
            Turnier löschen
        </a>
    </section>
</div>
</template>

<script>
import _ from 'underscore'
import base from 'base.vue'
import userlink from 'components/user-link.vue'
import dp from 'components/dp.vue'

export default {
    mixins: [ base ],
    components: { 'user-link': userlink, 'dp': dp },
    data () {
        return {
            tournament: {
                organizer: {},
                rankings: {}
            },
            groupFilter: 0
        }
    },
    computed: {
        // Dictionary of participants, with user ids as keys
        participants () {
            return _.object(_.map(this.tournament.invitations, i => {
                return [ i.user_id, i.invitee ]
            }))
        },
        // Participants, grouped by group
        groups () {
            var groups = [ ]
            var seen = [ ]

            _.each(this.tournament.matches, m => {
                if (m.group !== null) {
                    if (groups[m.group] === undefined) {
                        groups[m.group] = []
                    }

                    if (seen.indexOf(m.player_a_id) === -1) {
                        groups[m.group].push(m.player_a)
                        seen.push(m.player_a_id)
                    }

                    if (seen.indexOf(m.player_b_id) === -1) {
                        groups[m.group].push(m.player_b)
                        seen.push(m.player_b_id)
                    }
                }
            })

            return groups
        },
        winner () {
            return this.tournament.final_match.winner == 'a' ? 
                this.tournament.final_match.player_a : 
                this.tournament.final_match.player_b
        },
        second () {
            return this.tournament.final_match.winner == 'a' ? 
                this.tournament.final_match.player_b : 
                this.tournament.final_match.player_a
        }
    },
    methods: {
        fetchData () {
            const that = this

            return this.$api.get('tournament/' + this.$route.params.id)
            .then(response => {
                that.tournament = response.data
            })
        },
        deleteTournament () {
            if (!confirm(`Möchtest du wirklich das Turnier "${this.tournament.name}" löschen?`)) {
                return
            }

            const that = this
            this.$api.post(`tournament/${this.$route.params.id}/delete`)
            .then(response => {
                that.flash(`Das Turnier "${this.tournament.name}" wurde gelöscht`, 'success')
                that.$router.push('/tournaments')
            })
            .catch(error => {
                if (error.response && error.response.data.message) {
                    that.flash(error.response.data.message, 'error')
                } else {
                    throw error
                }
            })
        }
    },
    filters: {
        numToLetter (val) {
            return String.fromCharCode(65 + val)
        },
        roundName (val) {
            return [ 'Finale', 'Halbfinale', 'Viertelfinale', 'Achtelfinale' ][val]
        }
    }
}
</script>



// WEBPACK FOOTER //
// tournament-detail.vue?2318bce5